import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Bell, Clock, Mail, Shield, RefreshCcw, GitBranch } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import QawaemStatsCard from '../../products/saudi/components/QawaemStatsCard';
import { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { useSettings } from '../../core/contexts/SettingsContext';
import { syncApprobationsConfigToSettings } from './useApprobationsConfig';
import { DEFAULT_APPROBATIONS_CONFIG, normalizeApprobationsConfig } from './approbationsConfigDefaults';

const NS = 'configPage';
const API = '/api/approbations/config';

const DEFAULT_CONFIG = DEFAULT_APPROBATIONS_CONFIG;

export default function ApprobationsConfigPage() {
  const { t } = useTranslation();
  const { patchSettings } = useSettings();
  const p = (key, opts) => t(`modules.approbations.pages.${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const configEntityLabel = p('title', { defaultValue: 'إعدادات الموافقات' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await localApiAdapter.get(API);
        if (!cancelled) {
          const merged = normalizeApprobationsConfig(data || {});
          setConfig(merged);
          syncApprobationsConfigToSettings(merged, patchSettings);
        }
      } catch (error) {
        console.error('Failed to load approbations config:', error);
        if (!cancelled) {
          notifyError(showNotify, p('load_error'));
          setConfig(DEFAULT_CONFIG);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const reloadConfig = () => {
    setLoading(true);
    localApiAdapter.get(API)
      .then((data) => {
        const merged = normalizeApprobationsConfig(data || {});
        setConfig(merged);
        syncApprobationsConfigToSettings(merged, patchSettings);
      })
      .catch(() => setConfig(normalizeApprobationsConfig({})))
      .finally(() => setLoading(false));
  };

  const stats = [
    { titleKey: 'stat_sla', value: `${config.sla_hours}h`, icon: Clock, variant: 'copper' },
    { titleKey: 'stat_escalation', value: `${config.auto_escalation}h`, icon: Bell, variant: 'forest' },
    { titleKey: 'stat_email', value: config.email_notifications ? p('yes') : p('no'), icon: Mail, variant: 'sage' },
    { titleKey: 'stat_priority', value: config.default_priority, icon: Shield, variant: 'copper' },
    { titleKey: 'stat_reject_comment', value: config.require_comment_on_reject ? p('yes') : p('no'), icon: Settings, variant: 'forest' },
  ];

  const priorityOptions = [
    { value: 'basse', label: p('priority_low') },
    { value: 'normal', label: p('priority_normal') },
    { value: 'haute', label: p('priority_high') },
    { value: 'urgente', label: p('priority_urgent') },
  ];

  const updateConfig = (patch) => {
    setConfig((prev) => {
      const merged = normalizeApprobationsConfig({ ...prev, ...patch });
      syncApprobationsConfigToSettings(merged, patchSettings);
      return merged;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeApprobationsConfig(config);
      const updated = await localApiAdapter.put(API, payload);
      const merged = normalizeApprobationsConfig(updated || payload);
      setConfig(merged);
      syncApprobationsConfigToSettings(merged, patchSettings);
      setSaved(true);
      notifyUpdated(showNotify, configEntityLabel);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save approbations config:', error);
      notifyError(showNotify, error.message || p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const data = await localApiAdapter.post(`${API}/reset`, {});
      const merged = normalizeApprobationsConfig(data || {});
      setConfig(merged);
      syncApprobationsConfigToSettings(merged, patchSettings);
      notifyUpdated(showNotify, configEntityLabel);
    } catch (error) {
      console.error('Failed to reset approbations config:', error);
      notifyError(showNotify, error.message || p('reset_error'));
    }
  };

  const generalTabContent = (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
        <Settings className="h-5 w-5" />
        {p('section_general')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_sla')}</label>
          <input
            type="number"
            value={config.sla_hours}
            onChange={(e) => updateConfig({ sla_hours: e.target.value })}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
          />
        </div>
        <div>
          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_escalation')}</label>
          <input
            type="number"
            value={config.auto_escalation}
            onChange={(e) => updateConfig({ auto_escalation: e.target.value })}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
          />
        </div>
        <div>
          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_default_priority')}</label>
          <CustomSelect
            value={config.default_priority}
            onChange={(v) => updateConfig({ default_priority: v })}
            options={priorityOptions}
          />
        </div>
        <div>
          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_items_per_page', { defaultValue: 'عناصر لكل صفحة' })}</label>
          <input
            type="number"
            min="5"
            max="200"
            value={config.itemsPerPage}
            onChange={(e) => updateConfig({ itemsPerPage: e.target.value })}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
          />
        </div>
        <div className="flex flex-col gap-3 justify-center">
          <label className="flex items-center gap-2 text-white cursor-pointer">
            <input
              type="checkbox"
              checked={config.email_notifications}
              onChange={(e) => updateConfig({ email_notifications: e.target.checked })}
              className="rounded"
            />
            {p('field_email_notifications')}
          </label>
          <label className="flex items-center gap-2 text-white cursor-pointer">
            <input
              type="checkbox"
              checked={config.require_comment_on_reject}
              onChange={(e) => updateConfig({ require_comment_on_reject: e.target.checked })}
              className="rounded"
            />
            {p('field_reject_comment')}
          </label>
        </div>
      </div>
    </div>
  );

  const notificationsRulesTabContent = (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-4">
      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
        <Bell className="h-5 w-5" />
        {p('section_notifications_rules')}
      </h2>
      <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
        <GitBranch className="h-5 w-5 shrink-0 text-white/50 mt-0.5" />
        <p className="text-white/60">{p('notifications_rules_placeholder')}</p>
      </div>
    </div>
  );

  const moduleTabs = [
    { id: 'general', label: p('tab_general'), icon: Settings, gradient: 'from-blue-500 to-blue-600' },
    { id: 'notifications_rules', label: p('tab_notifications_rules'), icon: Bell, gradient: 'from-teal-500 to-teal-600' },
  ];

  const tabContent = {
    general: generalTabContent,
    notifications_rules: notificationsRulesTabContent,
  };

  const statsCards = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {sliceQawaemStats(stats, 4).map((stat, i) =>
        qawaem ? (
          <QawaemStatsCard key={stat.titleKey} label={p(stat.titleKey)} value={stat.value} icon={stat.icon} variant={stat.variant} delay={i * 0.08} />
        ) : (
          <motion.div key={stat.titleKey} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <p className="text-white/60 text-sm">{p(stat.titleKey)}</p>
            <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
          </motion.div>
        )
      )}
    </div>
  );

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={moduleTabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabContent={tabContent}
      loading={loading}
      saving={saving}
      saved={saved}
      savedMessage={p('saved_success')}
      onSave={handleSave}
      onReset={handleReset}
      showSaveActions={activeTab === 'general'}
      headerActions={(
        <button type="button" onClick={reloadConfig} className="refresh-button px-3 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white" title={t('common.refresh')}>
          <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      )}
    >
      {statsCards}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </ModuleConfigPageLayout>
  );
}
